import { useState } from 'react'
import Button from '../../../../components/common/Button'
import Alert from '../../../../components/common/Alert'

/**
 * Input form for traffic volume data
 * Step 1: User enters intersection ID, 12 volume movements, and 2 street names
 */
function VolumeFigureInputForm({ step, onSubmit, loading = false }) {
  // Pre-fill with existing data if available (e.g., when adjusting from preview)
  const defaultData = {
    intersection_id: '',
    volumes: {
      NBL: '',
      NBT: '',
      NBR: '',
      SBL: '',
      SBT: '',
      SBR: '',
      EBL: '',
      EBT: '',
      EBR: '',
      WBL: '',
      WBT: '',
      WBR: '',
    },
    streets: {
      STREET_BOTTOM: '',
      STREET_RIGHT: '',
    },
  }

  // Merge step input_data with defaults (handles empty object case)
  const initialData = {
    intersection_id: step.input_data?.intersection_id || '',
    volumes: { ...defaultData.volumes, ...(step.input_data?.volumes || {}) },
    streets: { ...defaultData.streets, ...(step.input_data?.streets || {}) },
  }

  const [formData, setFormData] = useState(initialData)
  const [errors, setErrors] = useState({})

  const validateVolumeFormat = (value) => {
    // Validate XX(YY) format
    const pattern = /^\d+\(\d+\)$/
    return pattern.test(value)
  }

  const handleVolumeChange = (movement, value) => {
    setFormData((prev) => ({
      ...prev,
      volumes: {
        ...prev.volumes,
        [movement]: value,
      },
    }))

    // Clear error for this field
    if (errors[movement]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[movement]
        return newErrors
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validate all fields
    const newErrors = {}

    if (!formData.intersection_id.trim()) {
      newErrors.intersection_id = 'Required'
    }

    Object.keys(formData.volumes).forEach((key) => {
      const value = formData.volumes[key].trim()
      if (!value) {
        newErrors[key] = 'Required'
      } else if (!validateVolumeFormat(value)) {
        newErrors[key] = 'Format: XX(YY)'
      }
    })

    if (!formData.streets.STREET_BOTTOM.trim()) {
      newErrors.STREET_BOTTOM = 'Required'
    }
    if (!formData.streets.STREET_RIGHT.trim()) {
      newErrors.STREET_RIGHT = 'Required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Submit form data directly (analysesApi.submitStep will wrap it)
    onSubmit(formData)
  }

  const movementGroups = [
    { title: 'Northbound', movements: ['NBL', 'NBT', 'NBR'] },
    { title: 'Southbound', movements: ['SBL', 'SBT', 'SBR'] },
    { title: 'Eastbound', movements: ['EBL', 'EBT', 'EBR'] },
    { title: 'Westbound', movements: ['WBL', 'WBT', 'WBR'] },
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Intersection ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Intersection ID</label>
        <input
          type="text"
          value={formData.intersection_id}
          onChange={(e) => setFormData({ ...formData, intersection_id: e.target.value })}
          className={`w-full px-3 py-2 border rounded-md ${
            errors.intersection_id ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="e.g., 10"
        />
        {errors.intersection_id && (
          <p className="text-red-500 text-sm mt-1">{errors.intersection_id}</p>
        )}
      </div>

      {/* Turn Movement Volumes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Turn Movement Volumes
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {movementGroups.map((group) => (
            <div key={group.title}>
              <h4 className="font-medium text-gray-700 mb-3">{group.title}</h4>
              <div className="space-y-2">
                {group.movements.map((movement) => (
                  <div key={movement}>
                    <label className="block text-xs text-gray-600 mb-1">{movement}</label>
                    <input
                      type="text"
                      value={formData.volumes[movement]}
                      onChange={(e) => handleVolumeChange(movement, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md text-sm ${
                        errors[movement] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0(2)"
                    />
                    {errors[movement] && (
                      <p className="text-red-500 text-xs mt-1">{errors[movement]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Street Names */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            East/West Street Name
          </label>
          <input
            type="text"
            value={formData.streets.STREET_BOTTOM}
            onChange={(e) =>
              setFormData({
                ...formData,
                streets: { ...formData.streets, STREET_BOTTOM: e.target.value },
              })
            }
            className={`w-full px-3 py-2 border rounded-md ${
              errors.STREET_BOTTOM ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., Private Driveway / York Blvd"
          />
          {errors.STREET_BOTTOM && (
            <p className="text-red-500 text-sm mt-1">{errors.STREET_BOTTOM}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            North/South Street Name
          </label>
          <input
            type="text"
            value={formData.streets.STREET_RIGHT}
            onChange={(e) =>
              setFormData({
                ...formData,
                streets: { ...formData.streets, STREET_RIGHT: e.target.value },
              })
            }
            className={`w-full px-3 py-2 border rounded-md ${
              errors.STREET_RIGHT ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., Stonelake Blvd"
          />
          {errors.STREET_RIGHT && (
            <p className="text-red-500 text-sm mt-1">{errors.STREET_RIGHT}</p>
          )}
        </div>
      </div>

      {/* Help Text */}
      <Alert
        variant="info"
        message={
          <>
            <strong>Format:</strong> Enter volumes as XX(YY) where XX is the AM peak hour volume and
            YY is the PM peak hour volume. For example: 78(253)
          </>
        }
      />

      {/* Actions */}
      <div className="flex gap-4">
        <Button type="button" variant="secondary" onClick={() => window.history.back()}>
          Cancel
        </Button>

        <Button type="submit" variant="primary" loading={loading} className="flex-1">
          {loading ? 'Generating Preview...' : 'Generate Preview'}
        </Button>
      </div>
    </form>
  )
}

export default VolumeFigureInputForm
